import { useState } from "react";
import {
  useDeleteEvent,
  useGetEventDeletePreview,
  getGetEventDeletePreviewQueryKey,
  getListEventsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only delete action for a single event. Opening the dialog fetches a
 * delete-preview that reports how much associated data the delete would destroy.
 * When any data exists, a prominent destructive warning block lists the counts
 * and a checkbox must be ticked before the (also-disabled) delete button
 * unlocks. When the event has no associated data, it is a plain "are you sure?".
 *
 * `onDeleted` is fired after a successful delete so the caller can navigate away
 * from / clear an open-event selection if needed; the events list query is
 * invalidated here regardless so the sidebar and Settings table drop the event.
 */
export function DeleteEventDialog({
  eventId,
  eventLabel,
  onDeleted,
}: {
  eventId: number;
  eventLabel: string;
  onDeleted?: (eventId: number) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const { data: preview, isLoading: previewLoading } = useGetEventDeletePreview(
    eventId,
    {
      query: {
        enabled: open,
        queryKey: getGetEventDeletePreviewQueryKey(eventId),
      },
    },
  );

  const del = useDeleteEvent({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListEventsQueryKey() });
        toast.success("Event deleted");
        setOpen(false);
        onDeleted?.(eventId);
      },
      onError: () => toast.error("Failed to delete event"),
    },
  });

  const hasData = !!preview?.hasData;
  // The delete button is gated on the acknowledgement checkbox only when the
  // event has data to destroy; a no-data event is a simple confirmation.
  const confirmDisabled =
    del.isPending || previewLoading || (hasData && !acknowledged);

  const impactLines: string[] = [];
  if (preview) {
    const add = (n: number, singular: string, plural: string) => {
      if (n > 0) impactLines.push(`${n} ${n === 1 ? singular : plural}`);
    };
    add(preview.inventoryCount, "listing", "listings");
    add(preview.ticketransferTaskCount, "Ticketransfer task", "Ticketransfer tasks");
    add(preview.ticketransferLinkCount, "delivery link", "delivery links");
    add(preview.skyboxEventCount, "Skybox mapping", "Skybox mappings");
    add(preview.skyboxInventoryCount, "Skybox listing", "Skybox listings");
    add(preview.skyboxSaleCount, "Skybox sale", "Skybox sales");
    add(preview.auditLogCount, "activity entry", "activity entries");
    add(preview.refreshRunCount, "refresh run", "refresh runs");
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setAcknowledged(false);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-7 rounded-full text-red-400 hover:text-red-300"
          aria-label="Delete event"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{eventLabel}”?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the event. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {previewLoading ? (
          <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking what would be deleted…
          </div>
        ) : hasData ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                <p className="font-semibold">
                  This event has data that will be permanently deleted:
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {impactLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(v) => setAcknowledged(v === true)}
                className="mt-0.5"
              />
              <span>
                I understand this will permanently delete the event and all the
                data listed above.
              </span>
            </label>
          </div>
        ) : (
          <p className="py-1 text-xs text-muted-foreground">
            This event has no associated data.
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={confirmDisabled}
            className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            onClick={(e) => {
              // Keep the dialog open until the mutation settles so the error
              // toast surfaces in context; success closes it via onSuccess.
              e.preventDefault();
              if (confirmDisabled) return;
              del.mutate({ id: eventId });
            }}
          >
            {del.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              "Delete event"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
